import type { CloudflareNavChild, CloudflareNavSection, HardcodedNavSection, ResolvedNavSection } from "./types";

function normTabKey(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

function pairKey(tab: string, subTab: string): string {
  return `${normTabKey(tab)}|${normTabKey(subTab)}`;
}

function cloudflareTabMatchesHardcodedSection(
  cloudflareTabTitle: string,
  hard: HardcodedNavSection
): boolean {
  const key = normTabKey(cloudflareTabTitle);
  if (!key) return false;
  if (key === normTabKey(hard.title)) return true;
  if (hard.collapsedTitle && key === normTabKey(hard.collapsedTitle)) return true;
  if (key === normTabKey(hard.id)) return true;
  return false;
}

function combineIsActive(
  a: (pathname: string) => boolean,
  b: (pathname: string) => boolean
): (pathname: string) => boolean {
  return (pathname: string) => a(pathname) || b(pathname);
}

function cloudflareOverridePairKeysForSection(
  section: HardcodedNavSection,
  cloudflare: CloudflareNavSection[],
  hardIds: Set<string>
): Set<string> {
  const out = new Set<string>();
  for (const cf of cloudflare) {
    if (hardIds.has(cf.id)) continue;
    if (!cloudflareTabMatchesHardcodedSection(cf.title, section)) continue;
    for (const ch of cf.children) {
      out.add(pairKey(ch.tabName, ch.subTab));
    }
  }
  return out;
}

function filterHardcodedForCloudflareOverrides(
  section: HardcodedNavSection,
  cloudflare: CloudflareNavSection[],
  hardIds: Set<string>
): HardcodedNavSection["children"] {
  const pairs = cloudflareOverridePairKeysForSection(section, cloudflare, hardIds);
  return section.children.filter((c) => {
    if (!c.overrideMatchTab || !c.overrideMatchSubTab) return true;
    const k = pairKey(c.overrideMatchTab, c.overrideMatchSubTab);
    return !pairs.has(k);
  });
}

function dedupeCloudflareNavChildren(
  existing: CloudflareNavChild[],
  incoming: CloudflareNavChild[]
): CloudflareNavChild[] {
  const seen = new Set<string>(existing.map((c) => pairKey(c.tabName, c.subTab)));
  const out: CloudflareNavChild[] = [];
  for (const c of incoming) {
    const k = pairKey(c.tabName, c.subTab);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
}

export function mergeNavSectionsWithOverride(
  hardcoded: HardcodedNavSection[],
  cloudflare: CloudflareNavSection[]
): ResolvedNavSection[] {
  const hardIds = new Set(hardcoded.map((s) => s.id));

  const resolved: ResolvedNavSection[] = hardcoded.map((h) => ({
    id: h.id,
    title: h.title,
    collapsedTitle: h.collapsedTitle,
    icon: h.icon,
    isSectionActive: h.isSectionActive,
    hardcodedChildren: filterHardcodedForCloudflareOverrides(h, cloudflare, hardIds),
    cloudflareChildren: [],
  }));

  const unmatchedCloudflare: CloudflareNavSection[] = [];

  for (const cf of cloudflare) {
    if (hardIds.has(cf.id)) {
      continue;
    }

    const matchingHard = hardcoded.find((h) => cloudflareTabMatchesHardcodedSection(cf.title, h));
    const target = matchingHard ? resolved.find((r) => r.id === matchingHard.id) : undefined;

    if (target && matchingHard) {
      const hard = matchingHard;
      const hardPaths = new Set(hard.children.map((c) => c.to));
      const extra = cf.children.filter((c) => !hardPaths.has(c.to));
      const deduped = dedupeCloudflareNavChildren(target.cloudflareChildren, extra);
      target.cloudflareChildren.push(...deduped);
      target.isSectionActive = combineIsActive(target.isSectionActive, cf.isSectionActive);
      continue;
    }

    unmatchedCloudflare.push(cf);
  }

  for (const cf of unmatchedCloudflare) {
    resolved.push({
      id: cf.id,
      title: cf.title,
      collapsedTitle: cf.title,
      icon: cf.icon,
      isSectionActive: cf.isSectionActive,
      hardcodedChildren: [],
      cloudflareChildren: cf.children,
    });
  }

  return resolved;
}

